/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useRef, useMemo } from 'react';

import classnames from 'classnames';

import Drawer from 'components/uiLibrary/Drawer';
import HorizontalScroller from 'components/uiLibrary/HorizontalScroller';
import Image from 'components/uiLibrary/Image';
import { TextButton } from 'components/uiLibrary/LinkButton';
import Loader from 'components/uiLibrary/Loader';
import Modal from 'components/uiLibrary/Modal';
import SpriteIcon from 'components/uiLibrary/SpriteIcon';
import Typography from 'components/uiLibrary/Typography';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SECTIONS, SUB_COMPONENTS } from 'components/Globals/Analytics/constants';
import CustomerLogos from 'components/Globals/CustomerLogos';
import LanguageSelector from 'components/Globals/LanguageSelector';
import Testimonials from 'components/Globals/Testimonials';
import { usePaywallContext } from 'components/Paywall';

import { NAV_LINKS } from 'constants/header';
import { TP, SORT_OPTION_VALUES } from 'constants/index';

import { createDate } from 'utils/date';
import useAppContext from 'utils/hooks/useAppContext';
import useDeviceTypeLayouts from 'utils/hooks/useDeviceTypeLayouts';
import { useDialogs } from 'utils/hooks/useDialogs';
import useNavigate from 'utils/hooks/useNavigate';
import { useQuery } from 'utils/react-query';

import queries from 'containers/Globals/queries';
import orgQueries from 'containers/Organizations/queries';

import { useTranslation } from 'src/i18n';

import { FILTER_TYPES, ROUTE_DATE_FORMATS, ROUTE_RESERVED_KEYWORDS } from 'operabase-router/constants';

import LogoImage from 'public/images/logo.png';

import { NavOptions } from '../NavLink';

import classes from './Menu.module.scss';

const menuTrackingData = {
  section: SECTIONS.MENU,
};

const language = { title: `${TP}.FN_LANGUAGE_LABEL`, type: 'lang' };

const today = createDate();

const PRO_TOOLS = [
  {
    title: `${TP}.FN_PRO_TOOL`,
    isUppercase: true,
    items: [
      { title: `${TP}.FN_CASTINGTOOL`, baseRoute: ROUTE_RESERVED_KEYWORDS.casting },
      { title: `${TP}.FN_PLAN_FEATURE_6`, baseRoute: ROUTE_RESERVED_KEYWORDS.artists },
      { title: `${TP}.m_RENTALS`, filters: { [FILTER_TYPES.RENTALS]: true } },
      { title: `${TP}.FN_CO_PRODUCED_CHECKBOX`, filters: { [FILTER_TYPES.RENTALS]: true } },
      { title: `${TP}.LP_AGENCY_ROSTER`, baseRoute: ROUTE_RESERVED_KEYWORDS.managers },
      { title: `${TP}.AUDIENCE_REACH`, baseRoute: ROUTE_RESERVED_KEYWORDS.proaudience },
      {
        title: `${TP}.LP_ARCHIVES`,
        filters: {
          [FILTER_TYPES.DATE]: {
            date_to: today.format(ROUTE_DATE_FORMATS.STANDARD),
            path: ROUTE_RESERVED_KEYWORDS.archives,
          },
        },
      },
      {
        title: `${TP}.ARTIST_PROFILE`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.artists}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
    ],
  },
  {
    title: `${TP}.PRO_TOOLS_FOR`,
    items: [
      {
        title: `${TP}.FN_ARTS_ORGANISATIONS`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.companies}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.PROMOTERS`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.companies}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.m_VENUES`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.companies}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.AGENCIES_MANAGERS`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.agencies}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.m_ARTISTS`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.artists}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.FN_PRO_INDUSTRY`,
        baseRoute: `${ROUTE_RESERVED_KEYWORDS.pro}/${ROUTE_RESERVED_KEYWORDS.artists}/${ROUTE_RESERVED_KEYWORDS.details}`,
      },
      {
        title: `${TP}.TICKETING_STREAMING_PLATFORMS`,
      },
      {
        title: `${TP}.RESEARCHERS_ACADEMICS`,
      },
      {
        title: `${TP}.JOURNALISTS_MEDIA`,
      },
    ],
  },
];

const ProTools = ({ listingView, isDrawer, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const navigate = useNavigate();
  const { setIsMenuOpen } = useDialogs();

  if (isDrawer) {
    return (
      <div className={classes.proToolsDrawer}>
        {PRO_TOOLS.map((tool, index) => (
          <>
            <div key={tool.title} className={classes.proToolsDrawer__content}>
              <Typography size="14" weight="bold" textTransform="capitalize">
                {t(tool.title)}
              </Typography>
              <div className={classes.proToolsDrawer__items}>
                {tool.items.map(feature => (
                  <TextButton
                    key={feature.title}
                    styles={{ root: classes.proToolsDrawer__item }}
                    disabled={!feature.baseRoute}
                    isLink
                    {...navigate.getLinkProps({
                      ...(feature?.baseRoute && {
                        baseRoute: feature?.baseRoute,
                      }),
                      ...(feature?.filters && {
                        filters: {
                          ...feature?.filters,
                        },
                      }),
                    })}
                    onClick={() => {
                      setIsMenuOpen({ isOpen: false });
                    }}
                    trackingData={{
                      ...trackingData,
                      component: COMPONENTS.PRO_TOOLS,
                      subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
                    }}
                  >
                    <Typography
                      size="12"
                      {...(tool?.isUppercase && { textTransform: 'uppercase' })}
                      className={classes.proToolsDrawer__itemTitle}
                      truncate
                    >
                      {t(feature.title)}
                    </Typography>
                    <SpriteIcon icon="chevron_right" size="14" className={classes.proToolsDrawer__arrowIcon} />
                  </TextButton>
                ))}
              </div>
            </div>
            {index !== PRO_TOOLS.length - 1 && <div className={classes.proToolsDrawer__divider} />}
          </>
        ))}
      </div>
    );
  }

  return (
    <div className={classes.proToolsWrapper}>
      {!listingView && (
        <Typography weight="bold" textTransform="capitalize" className={classes.proToolsWrapper__title}>
          {t(`${TP}.FOR_PROFESSIONALS`)}
        </Typography>
      )}
      <div className={classnames(classes.proTools, { [classes.proTools_listingView]: listingView })}>
        {PRO_TOOLS.map(tool => (
          <div key={tool.title} className={classes.proTools__content}>
            <Typography size="14" weight="bold" textTransform="capitalize">
              {t(tool.title)}
            </Typography>
            <ul className={classes.proTools__items}>
              {tool.items.map(feature => (
                <li key={feature.title}>
                  <TextButton
                    styles={{ root: classes.proTools__item }}
                    disabled={!feature.baseRoute && !feature.filters}
                    isLink
                    {...navigate.getLinkProps({
                      ...(feature?.baseRoute && {
                        baseRoute: feature?.baseRoute,
                      }),
                      ...(feature?.filters && {
                        filters: {
                          ...feature?.filters,
                        },
                      }),
                    })}
                    onClick={() => {
                      setIsMenuOpen({ isOpen: false });
                    }}
                    trackingData={{
                      ...trackingData,
                      component: COMPONENTS.PRO_TOOLS,
                      subComponent: SUB_COMPONENTS.NAVIGATE_CTA,
                    }}
                  >
                    <Typography
                      size="12"
                      {...(tool?.isUppercase && { textTransform: 'uppercase' })}
                      className={classes.proTools__itemTitle}
                      truncate
                    >
                      {t(feature.title)}
                    </Typography>
                    <SpriteIcon icon="chevron_right" size="14" className={classes.proTools__arrowIcon} />
                  </TextButton>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const MenuItem = ({ title, total, data, path, onClose, trackingData = {}, listingView, ...rest }) => (
  <div className={classnames(classes.menuItem, { [classes.menuItem_listingView]: listingView })}>
    <NavOptions
      data={data}
      total={total}
      path={path}
      title={title}
      limit={7}
      isMenu
      onClose={onClose}
      trackingData={trackingData}
      listingView={listingView}
      {...rest}
    />
  </div>
);

const DrawerItem = ({ title, total, data, path, onClose, trackingData = {}, selectedType, ...rest }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');

  if (selectedType === 'pro_tools') {
    return <ProTools isDrawer trackingData={trackingData} />;
  }

  if (selectedType === language.type) {
    return (
      <div>
        <Typography size="13" weight="bold">
          {`${t(`${TP}.m_LANGUAGES`)}`}
        </Typography>
        <LanguageSelector renderAs="content" type="menu" />
      </div>
    );
  }

  return (
    <div className={classes.drawerItem}>
      <NavOptions
        data={data}
        total={total}
        path={path}
        title={title}
        limit={15}
        isDrawer
        onClose={onClose}
        trackingData={trackingData}
        {...rest}
      />
    </div>
  );
};

const DrawerContent = ({ isLoading, trackingData, onClose, links, listingView }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();
  const { isMenuOpen } = useDialogs();
  const { isProTools } = isMenuOpen;
  const [selectedType, setSelectedType] = useState(isProTools ? 'pro_tools' : links[0]?.type);

  const selectedOption = useMemo(() => {
    if (selectedType === language.type) {
      return language;
    }

    return links.find(link => link.type === selectedType);
  }, [links, selectedType]);

  const drawerRef = useRef(null);

  const scrollToTop = () => {
    if (drawerRef.current) {
      drawerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  if (isLoading) {
    return <Loader className={classes.loaderMobile} />;
  }

  return (
    <div ref={drawerRef}>
      {!listingView && (
        <div className={classes.drawer__header}>
          <Typography size="16" weight="bold">
            {t(`${TP}.MENU`)}
          </Typography>
          <TextButton
            trackingData={{
              ...trackingData,
              component: COMPONENTS.CLOSE_CTA,
            }}
            onClick={onClose}
            leftIcon={<SpriteIcon icon="modal_close" />}
            disableUnderline
          >
            <Typography weight="bold">{t(`${TP}.CLOSE`)}</Typography>
          </TextButton>
        </div>
      )}
      <div className={classes.drawer__content}>
        <div className={classes.drawer__leftSection}>
          <Typography
            size="13"
            weight="bold"
            truncate
            className={classnames(classes.drawer__groupTitle, {
              [classes.drawer__groupTitleSelected]: selectedType === 'pro_tools',
            })}
          >
            {t(`${TP}.FOR_PROFESSIONALS`)}
          </Typography>
          <div
            className={classnames(classes.drawer__leftSection_item, {
              [classes.drawer__leftSection_selected]: selectedType === 'pro_tools',
            })}
            onClick={() => {
              track.click({
                ...trackingData,
                component: COMPONENTS.MENU_ITEM,
                meta: {
                  id: 'pro_tools',
                },
              });
              setSelectedType('pro_tools');
              scrollToTop();
            }}
          >
            <Typography size="12">{t(`${TP}.FN_PRO_TOOL`)}</Typography>
          </div>
          <Typography
            size="13"
            weight="bold"
            truncate
            className={classnames(classes.drawer__groupTitle, {
              [classes.drawer__groupTitleSelected]: selectedType === links[0]?.type,
            })}
          >
            {t(`${TP}.PROAUDIENCE`)}
          </Typography>
          {links.map(link => (
            <div
              key={link.title}
              className={classnames(classes.drawer__leftSection_item, {
                [classes.drawer__leftSection_selected]: selectedType === link?.type,
              })}
              onClick={() => {
                track.click({
                  ...trackingData,
                  component: COMPONENTS.MENU_ITEM,
                  meta: {
                    id: link?.as,
                  },
                });
                setSelectedType(link.type);
                scrollToTop();
              }}
            >
              <Typography size="12">{link.title}</Typography>
              {link.total > 0 && (
                <Typography size="11" color="secondary">
                  {link.total}
                </Typography>
              )}
            </div>
          ))}
          <div
            className={classnames(classes.drawer__leftSection_item, {
              [classes.drawer__leftSection_selected]: selectedType === language.type,
            })}
            onClick={() => {
              track.click({
                ...trackingData,
                component: COMPONENTS.MENU_ITEM,
                meta: {
                  id: 'language',
                },
              });
              setSelectedType(language.type);
              scrollToTop();
            }}
          >
            <Typography size="12">{t(language.title)}</Typography>
          </div>
        </div>
        <div className={classes.drawer__rightSection}>
          <DrawerItem onClose={onClose} trackingData={trackingData} {...selectedOption} selectedType={selectedType} />
        </div>
      </div>
    </div>
  );
};

const ViewAllOrgs = ({ trackingData }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isLoggedIn } = useAppContext();
  const { setShowSubscriptionPlans } = usePaywallContext();
  const { setIsMenuOpen, setIsLoginDialog } = useDialogs();

  const handleViewAll = () => {
    setIsMenuOpen({ isOpen: false });

    if (isLoggedIn) {
      setShowSubscriptionPlans({
        show: true,
      });
    } else {
      setIsLoginDialog({ isOpen: true });
    }
  };

  return (
    <TextButton
      rightIcon={<SpriteIcon icon="chevron_right" size={12} className={classes.arrowIcon} />}
      styles={{ root: classes.viewAllBtn }}
      disableUnderline
      onClick={handleViewAll}
      trackingData={{
        ...trackingData,
        component: COMPONENTS.VIEW_ALL_CUSTOMERS_AND_TESTIMONIALS,
      }}
    >
      <Typography size={12} color="link">
        {t(`${TP}.VIEW_COMPLETE_LIST_CTA`)}
      </Typography>
    </TextButton>
  );
};

const TrustedBy = ({ limit = 5, trackingData = {} }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { data: organizations } = useQuery(
    orgQueries.getOrganizations({
      limit,
      filters: {
        sort: SORT_OPTION_VALUES.PRODUCTION_COUNT_DESC,
        is_pro: true,
      },
      queryConfig: {
        select: response => response?.data.map(org => org.name),
      },
    }),
  );

  return (
    <HorizontalScroller hideArrow>
      <div className={classes.trustedBy}>
        <Typography size={12} secondaryFont className={classes.trustedBy__title}>
          {t('FN_TRUSTED_BY_TITLE')}
        </Typography>
        <div className={classes.trustedByItems}>
          {organizations?.map(orgName => (
            <Typography size={12} key={orgName} className={classes.trustedBy__orgName}>
              {orgName}
            </Typography>
          ))}
          <ViewAllOrgs trackingData={trackingData} />
        </div>
      </div>
    </HorizontalScroller>
  );
};

const MenuDrawer = ({ links, onClose, trackingData, isLoading }) => (
  <Drawer isOpen onClose={onClose} className={classes.drawer}>
    <DrawerContent isLoading={isLoading} trackingData={trackingData} onClose={onClose} links={links} />
  </Drawer>
);

const MenuContent = ({ isLoading, trackingData, onClose, links, listingView }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const track = useTracking();

  if (isLoading) {
    return <Loader className={classes.loader} />;
  }

  return (
    <div className={classes.menu}>
      {!listingView && (
        <div className={classes.menu__header}>
          <div className={classes.menu__headerLeftSection}>
            <TextButton
              trackingData={{
                ...trackingData,
                component: COMPONENTS.BACK_CTA,
              }}
              leftIcon={<SpriteIcon icon="arrow_back" size="14" className={classes.icon} />}
              disableUnderline
              onClick={onClose}
            >
              <Typography weight="bold">{t(`${TP}.MENU`)}</Typography>
            </TextButton>
            <div className={classes.menu__headerContent}>
              <div className={classes.menu__logoSection}>
                <Image
                  src={LogoImage}
                  alt="Operabase Home"
                  lazy={false}
                  className={classes.menu__logoImage}
                  disableCloudinary
                  disableNextImage
                />
                <Typography size="11">{t(`${TP}.LP_SINCE`)}</Typography>
                <Typography size="12">{t(`${TP}.FN_GLOBAL_REFERENCE_PERFORMING_ARTS`)}</Typography>
              </div>
              <TrustedBy trackingData={menuTrackingData} />
            </div>
          </div>
          <SpriteIcon
            icon="close"
            size="18"
            className={classes.icon}
            onClick={() => {
              track.click({
                ...trackingData,
                component: COMPONENTS.CLOSE_CTA,
              });
              onClose();
            }}
          />
        </div>
      )}
      <div
        className={classnames(classes.menu__contentWrapper, {
          [classes.menu__contentWrapper_listingView]: listingView,
        })}
      >
        <ProTools listingView={listingView} trackingData={trackingData} />
        <div className={classes.proAudienceWrapper}>
          {!listingView && (
            <Typography weight="bold" textTransform="capitalize">
              {t(`${TP}.PROAUDIENCE`)}
            </Typography>
          )}
          <div className={classnames(classes.menu__content, { [classes.menu__contentPage]: listingView })}>
            {links.map(link => (
              <MenuItem
                key={link.title}
                onClose={onClose}
                trackingData={trackingData}
                {...link}
                listingView={listingView}
              />
            ))}
          </div>
        </div>
      </div>
      {!listingView && (
        <div className={classes.menu__footer}>
          <Typography size="12" secondaryFont>
            {t(`${TP}.TRUSTED_AND_USED_BY`)}
          </Typography>
          <div className={classes.menu__footer_logos}>
            <CustomerLogos allowShowAll={false} styles={{ logoList: classes.menu__footer_logosList }} />
          </div>
          <Testimonials limit={1} inLine hideTitle hideBorder />
        </div>
      )}
    </div>
  );
};

const MenuModal = ({ links, onClose, trackingData, isLoading }) => (
  <Modal isOpen onClose={onClose} styles={{ modalContainer: classes.modalContainer, modalRoot: classes.modalRoot }}>
    <MenuContent isLoading={isLoading} trackingData={trackingData} onClose={onClose} links={links} />
  </Modal>
);

const Menu = ({ onClose, listingView = false }) => {
  const { t } = useTranslation('NS_APP_GLOBALS');
  const { isDesktop } = useDeviceTypeLayouts();
  const { data: statsNumbers } = useQuery(queries.getHomeNumbers());

  const links = useMemo(
    () =>
      Object.entries(NAV_LINKS).map(([key, link]) => ({
        ...link,
        title: t(link.title),
        total: statsNumbers?.data?.[key]?.total || 0,
        type: key,
      })),
    [statsNumbers, t],
  );

  const { data: menuItems, isFetching: isLoading } = useQuery(queries.getMenuItems({ type: 'ALL' }));

  const linkesWithMenuItems = useMemo(() => links?.map(link => ({ ...link, data: menuItems?.data?.[link.type] })), [
    links,
    menuItems?.data,
  ]);

  if (listingView) {
    return (
      <MenuContent
        isLoading={isLoading}
        trackingData={{
          section: SECTIONS.MENU_LISTING,
        }}
        onClose={onClose}
        links={linkesWithMenuItems}
        listingView
      />
    );
  }

  if (isDesktop) {
    return (
      <MenuModal onClose={onClose} links={linkesWithMenuItems} trackingData={menuTrackingData} isLoading={isLoading} />
    );
  }

  return (
    <MenuDrawer onClose={onClose} links={linkesWithMenuItems} trackingData={menuTrackingData} isLoading={isLoading} />
  );
};

export default Menu;
